/**
 * Utility functions for handling profession display and localization
 */

/**
 * Determines if Indonesian/Malay names should be used based on country
 * @param {string} country - Country code (e.g., 'ID', 'MY', 'SG', 'PH')
 * @returns {boolean} - True if Indonesian/Malay names should be used
 */
export const shouldUseIndonesianName = (country) => {
  const upperCountry = country?.toUpperCase();
  return upperCountry === 'ID' || upperCountry === 'INDONESIA';
};

/**
 * Gets the localized display name for a profession based on country
 * @param {object} profession - Profession object from API
 * @param {string} country - Country code
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {string} - Localized profession name
 */
export const getLocalizedProfessionName = (profession, country, jobType) => {
  if (!profession) return '';
  
  // For International job type (id: 4), always return international name
  if (jobType === 4) {
    return profession.international_name || '';
  }
  
  // Check if we have the localized_name field (from country-specific endpoint)
  if (profession.localized_name) {
    return profession.localized_name;
  }
  
  // For Indonesia, use Indonesian/Malay names
  if (shouldUseIndonesianName(country)) {
    return profession.indonesia_name || 
           profession.malay_name || 
           profession.international_name || 
           '';
  }
  
  // For other countries, map to specific names or use international
  const upperCountry = country?.toUpperCase();
  switch (upperCountry) {
    case 'MY':
    case 'MALAYSIA':
      return profession.malaysia_name || profession.international_name || '';
    case 'SG':
    case 'SINGAPORE':
      return profession.singapore_name || profession.international_name || '';
    case 'PH':
    case 'PHILIPPINES':
      return profession.philippines_name || profession.international_name || '';
    default:
      return profession.international_name || '';
  }
};

/**
 * Gets the profession type name in the appropriate language
 * @param {object} professionType - Profession type object
 * @param {string} country - Country code
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {string} - Localized profession type name
 */
export const getLocalizedProfessionTypeName = (professionType, country, jobType) => {
  if (!professionType) return '';
  
  // For International job type (id: 4), always return english name
  if (jobType === 4) {
    return professionType.english_name || '';
  }
  
  if (shouldUseIndonesianName(country)) {
    return professionType.malay_name || professionType.english_name || '';
  }
  
  return professionType.english_name || '';
};

/**
 * Formats profession with category for display
 * @param {object} profession - Profession object
 * @param {string} country - Country code
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {string} - Formatted profession string
 */
export const formatProfessionWithCategory = (profession, country, jobType) => {
  if (!profession) return '';
  
  const professionName = getLocalizedProfessionName(profession, country, jobType);
  
  if (profession.profession_type) {
    const categoryName = getLocalizedProfessionTypeName(profession.profession_type, country, jobType);
    return `${categoryName} - ${professionName}`;
  }
  
  return professionName;
};

/**
 * Extracts title and subtitle from profession name with brackets
 * Takes the LAST bracket content as subtitle and preserves other brackets in title
 * Removes any dash before the last bracket
 * @param {string} professionName - Full profession name with potential brackets
 * @returns {object} - Object with title and subtitle properties
 * @example
 * extractProfessionTitleAndSubtitle("Nurse (Pediatric)") 
 * // returns { title: "Nurse", subtitle: "Pediatric" }
 * extractProfessionTitleAndSubtitle("test (one) - (two)") 
 * // returns { title: "test (one)", subtitle: "two" }
 * extractProfessionTitleAndSubtitle("Nurse (General) - (Senior)") 
 * // returns { title: "Nurse (General)", subtitle: "Senior" }
 * extractProfessionTitleAndSubtitle("Doctor (A) (B) (C)") 
 * // returns { title: "Doctor (A) (B)", subtitle: "C" }
 * extractProfessionTitleAndSubtitle("Senior Nurse") 
 * // returns { title: "Senior Nurse", subtitle: "" }
 */
export const extractProfessionTitleAndSubtitle = (professionName) => {
  if (!professionName) return { title: '', subtitle: '' };
  
  // Match all content within parentheses
  const bracketPattern = /\([^)]+\)/g;
  const matches = professionName.match(bracketPattern);
  
  if (!matches || matches.length === 0) {
    // No brackets found, return the whole name as title
    return { title: professionName, subtitle: '' };
  }
  
  // Get the last bracket match
  const lastBracket = matches[matches.length - 1];
  
  // Extract the content from the last bracket (remove the parentheses)
  const subtitle = lastBracket.replace(/[()]/g, '').trim();
  
  // Find the index where the last bracket starts
  const lastBracketIndex = professionName.lastIndexOf(lastBracket);
  
  // Extract everything before the last bracket as the title
  let title = professionName.substring(0, lastBracketIndex).trim();
  
  // Remove any trailing dash (with optional spaces) before the last bracket
  title = title.replace(/\s*-\s*$/, '').trim();
  
  // Clean up multiple spaces that might result
  title = title.replace(/\s+/g, ' ').trim();
  
  return { title, subtitle };
};

/**
 * Filters professions based on search text
 * @param {array} professions - Array of profession objects
 * @param {string} searchText - Search query
 * @param {string} country - Country code for localized search
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {array} - Filtered professions
 */
export const filterProfessions = (professions, searchText, country, jobType) => {
  if (!searchText || !professions) return professions;
  
  const lowerSearch = searchText.toLowerCase();
  
  return professions.filter(profession => {
    // Search in localized name
    const localizedName = getLocalizedProfessionName(profession, country, jobType);
    if (localizedName.toLowerCase().includes(lowerSearch)) return true;
    
    // Also search in international name
    if (profession.international_name?.toLowerCase().includes(lowerSearch)) return true;
    
    // Search in profession type
    if (profession.profession_type) {
      const typeName = getLocalizedProfessionTypeName(profession.profession_type, country, jobType);
      if (typeName.toLowerCase().includes(lowerSearch)) return true;
    }
    
    return false;
  });
};
/**
 * Profession type codes whose lists are specialist-only. "General <specialty>"
 * entries here (e.g. "General Surgeon") are specialties in their own right, not
 * the generalist fallback for the list, so they are left in their normal order.
 */
const SPECIALIST_TYPE_CODES = ['a-b', 'c-b', 'd-b'];

/**
 * Profession codes that are the generalist entry of their list but whose name
 * does not start with "General" (the marker sits inside the brackets instead).
 */
const GENERAL_PROFESSION_CODES = [
  'b-a-65', // Registered Nurse (RN/ General Staff Nurse)
  'e-x-5',  // Pharmacist (General License)
];

/**
 * Reads a profession's own type code, whichever shape the endpoint returned.
 * @param {object} profession - Profession object from API
 * @returns {string} - Profession type code, or '' when absent
 */
const getProfessionTypeCode = (profession) =>
  profession?.profession_type_code || profession?.profession_type?.code || '';

/**
 * Determines whether a profession is the "general" entry of its list.
 * Matches on the title (the part before the trailing bracket) so that
 * "Cardiologist (General / Non-Interventional)" is not treated as general,
 * plus an explicit allowlist for entries that carry the marker in brackets.
 * Specialist lists are excluded, so "General Surgeon" stays in place even when
 * it sits in a list flattened up to its parent type.
 * @param {object} profession - Profession object from API
 * @param {string} country - Country code
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {boolean} - True if the profession should be pinned to the top
 */
export const isGeneralProfession = (profession, country, jobType) => {
  if (!profession) return false;

  if (SPECIALIST_TYPE_CODES.includes(getProfessionTypeCode(profession))) return false;

  if (GENERAL_PROFESSION_CODES.includes(profession.code)) return true;

  const names = [
    getLocalizedProfessionName(profession, country, jobType),
    profession.international_name,
  ];

  return names.some((name) => {
    if (!name) return false;
    const { title } = extractProfessionTitleAndSubtitle(name);
    return /^general\b/i.test(title.trim());
  });
};

/**
 * Sorts a profession list so its "general" entry sits first, leaving the
 * remaining entries in the order they were given (so an already-alphabetical
 * list stays alphabetical below the pinned entry).
 * @param {array} professions - Professions to order
 * @param {string} [typeCode] - Profession type code the list belongs to.
 *   Optional — when omitted each profession's own type code is used, which is
 *   what mixed lists (flattened to a parent type) need.
 * @param {string} country - Country code
 * @param {number} jobType - Job type ID (4 = International)
 * @returns {array} - New array with general professions pinned to the top
 */
export const sortProfessionsGeneralFirst = (professions, typeCode, country, jobType) => {
  if (!professions?.length) return professions;
  if (typeCode && SPECIALIST_TYPE_CODES.includes(typeCode)) return professions;

  const general = [];
  const rest = [];

  professions.forEach((profession) => {
    if (isGeneralProfession(profession, country, jobType)) {
      general.push(profession);
    } else {
      rest.push(profession);
    }
  });

  if (!general.length) return professions;

  return [...general, ...rest];
};
